<template>
  <div ref="chart" class="bottom-entry" v-if="showChart">
    <apexchart
      id="chart"
      height="225"
      ref="realtimeChart"
      type="line"
      :options="chartOptions"
      :series="series"
    ></apexchart>
  </div>
</template>

<script>
import actions from '../../store/actions';

export default {
  name: 'DailyChart',
  data() {
    return {
      series: [
        {
          name: this.$t('chart.deaths'),
          data: [],
        },
        {
          name: this.$t('chart.recovered'),
          data: [],
        },
        {
          name: this.$t('chart.confirmed'),
          data: [],
        },
      ],
      chartOptions: {
        chart: {
          background: 'rgba(0,0,0,0)',
          type: 'area',
          stacked: true,
          toolbar: {
            show: true,
            tools: {
              download: false,
              selection: true,
              zoom: true,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: true,
            },
          },
          events: {
            click: (e, chart, { dataPointIndex }) => {
              if (dataPointIndex !== -1) {
                this.addDayAnnotation(dataPointIndex);
                this.$store.dispatch(actions.CHANGE_CURRENT_DATA_INDEX, dataPointIndex);
              }
            },
            zoomed: (chartContext, { xaxis }) => {
              if (xaxis.min && xaxis.max) {
                const fromDateIndex = this.getDateIndex(new Date(xaxis.min).toDateString()) + 1;
                let toDateIndex = this.getDateIndex(new Date(xaxis.max).toDateString());
                if (toDateIndex === -1) {
                  if (this.countryList.length - 1 !== -1) {
                    toDateIndex = this.countryList.length - 1;
                  } else {
                    toDateIndex = this.globalDataList.length - 1;
                  }
                }
                this.$store.dispatch(actions.CHANGE_DATA_RANGE, [fromDateIndex, toDateIndex]);
              } else {
                this.$store.dispatch(actions.CHANGE_DATA_RANGE, [
                  0,
                  this.globalDataList.length - 1,
                ]);
              }
            },
          },
        },
        fill: {
          gradient: {
            enabled: true,
            opacityFrom: 0.55,
            opacityTo: 0,
          },
        },
        selection: {
          enabled: true,
          type: 'x',
          fill: {
            color: '#24292e',
            opacity: 0.1,
          },
          stroke: {
            width: 1,
            dashArray: 3,
            color: '#24292e',
            opacity: 0.4,
          },
          xaxis: {
            min: undefined,
            max: undefined,
          },
        },
        tooltip: {
          custom: () => null,
        },
        colors: ['#fa5b5a', '#8cff57', '#23a3c0'],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          curve: 'straight',
          lineCap: 'butt',
          width: 2,
          dashArray: 0,
        },
        title: {
          text: `${this.$t('chart.title')}`,
          align: 'left',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: undefined,
            color: 'rgb(252,255,255)',
          },
        },
        grid: {
          show: false,
          padding: {
            left: 0,
            right: 50,
          },
        },
        xaxis: {
          type: 'datetime',
          categories: [],
          labels: {
            show: true,
            rotateAlways: false,
            hideOverlappingLabels: true,
            showDuplicates: false,
            trim: false,
            maxHeight: 120,
            style: {
              colors: '#9A9A9A',
              fontWeight: 400,
            },
            offsetX: 0,
            offsetY: 0,
            datetimeUTC: true,
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm',
            },
          },
          axisBorder: {
            show: true,
            color: '#fffafe',
            height: 1,
            width: '100%',
            offsetX: 0,
            offsetY: 0,
          },
          axisTicks: {
            show: true,
            borderType: 'solid',
            color: '#fffafe',
            height: 6,
            offsetX: -5,
            offsetY: 0,
          },
          position: 'bottom',
          title: {
            text: this.$t('chart.xlabel'),
            offsetX: 0,
            offsetY: 10,
            style: {
              color: '#fff',
              fontSize: '12px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontWeight: 600,
            },
          },
          tickAmount: 'dataPoints',
          tooltip: {
            enabled: true,
            formatter(value) {
              const dateObj = new Date(value);
              const year = dateObj.getUTCFullYear();
              const month = dateObj.getUTCMonth() + 1;
              const day = dateObj.getUTCDate();
              return `${day}/${month}/${year}`;
            },
          },
        },
        yaxis: {
          type: 'numeric',
          style: {
            color: 'rgb(252,255,255)',
          },
          labels: {
            show: true,
            rotateAlways: false,
            hideOverlappingLabels: true,
            showDuplicates: false,
            trim: false,
            minHeight: undefined,
            maxHeight: 120,
            style: {
              colors: '#9A9A9A',
              fontWeight: 400,
            },
            formatter(value) {
              return Number(value).toLocaleString();
            },
          },
          axisTicks: {
            show: true,
            borderType: 'solid',
            color: '#fffafe',
            height: 6,
            offsetX: 0,
            offsetY: 0,
          },
          title: {
            text: this.$t('chart.ylabel'),
            offsetX: 0,
            offsetY: 0,
            style: {
              color: '#fff',
              fontSize: '12px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontWeight: 600,
            },
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          floating: true,
          offsetY: -12,
          offsetX: 0,
          labels: {
            useSeriesColors: 'true',
          },
        },
        annotations: {
          xaxis: [],
        },
      },
      countryList: [],
      globalDataList: [],
      countryDataList: [],
      radiusDataList: [],
      userLocale: 'en',
    };
  },
  computed: {
    currentDataIndex() {
      return this.$store.state.currentDataIndex;
    },
    globalData() {
      return this.$store.state.globalData;
    },
    countryData() {
      return this.$store.state.countryData;
    },
    radiusData() {
      return this.$store.state.radiusData;
    },
    country() {
      return this.$store.state.country;
    },
    showChart() {
      return this.$store.state.showChart;
    },
    radius() {
      return this.$store.state.radius;
    },
    selectedCountry() {
      return this.$store.state.selectedCountry;
    },
    useRadius() {
      return this.$store.state.useRadius;
    },
    locale() {
      return this.$i18n.locale;
    },
  },
  watch: {
    currentDataIndex(currentDataIndex) {
      this.addDayAnnotation(currentDataIndex);
    },
    globalData(countryList) {
      this.globalDataList = countryList;
      this.formatGlobalEntrySeries(countryList);
      this.$refs.realtimeChart.updateOptions(this.chartOptions);
    },
    radiusData(radiusData) {
      this.radiusDataList = radiusData;
      this.formatRangeEntrySeries(radiusData);
      this.$refs.realtimeChart.updateOptions(this.chartOptions);
    },
    selectedCountry() {
      const country = this.$store.state.selectedCountry;
      this.$store.dispatch(actions.CHANGE_DATA_RANGE, [0, this.globalDataList.length - 1]);
      if (country && this.$store.state.useRadius) {
        this.$store.dispatch(actions.LOAD_RADIUS_DATA, {
          lat: country.lat,
          lon: country.lon,
          radius: this.$store.state.radius,
        });
      } else {
        this.formatGlobalEntrySeries(this.globalDataList);
        this.$refs.realtimeChart.updateOptions(this.chartOptions);
      }
    },
    useRadius(change) {
      const country = this.$store.state.selectedCountry;
      if (country && change) {
        this.$store.dispatch(actions.LOAD_RADIUS_DATA, {
          lat: country.lat,
          lon: country.lon,
          radius: this.$store.state.radius,
        });
      } else if (!change) {
        this.formatGlobalEntrySeries(this.globalDataList);
        this.$refs.realtimeChart.updateOptions(this.chartOptions);
      }
      this.$store.dispatch(actions.CHANGE_DATA_RANGE, [0, this.globalDataList.length - 1]);
    },
    locale() {
      this.chartOptions.title.text = `${this.$t('chart.title')}`;
      this.chartOptions.yaxis[0].title.text = `${this.$t('chart.ylabel')}`;
      this.chartOptions.xaxis.title.text = `${this.$t('chart.xlabel')}`;

      this.formatGlobalEntrySeries(this.globalDataList);
      this.$refs.realtimeChart.updateOptions(this.chartOptions);
    },
  },
  methods: {
    addDayAnnotation(currentDataIndex) {
      if (this.countryList.length !== 0 || this.$store.state.globalData.length !== 0) {
        const currentDay = new Date(`${this.$store.state.globalData[currentDataIndex].day} GMT`);
        if (this.$refs.realtimeChart) {
          this.$refs.realtimeChart.clearAnnotations();
          this.$refs.realtimeChart.addXaxisAnnotation({
            x: currentDay.getTime(),
            strokeDashArray: 0,
            borderColor: '#775DD0',
            label: {
              borderColor: '#775DD0',
              orientation: 'horizontal',
              style: {
                color: '#fff',
                background: '#775DD0',
              },
              text: currentDay.toDateString(),
            },
          });
        }
      }
    },
    formatRangeEntrySeries(entries) {
      this.removeSeries();
      entries.daily_totals.forEach((x) => {
        this.series.filter((s) => s.name === 'Recovered')[0].data.push([x.day, x.total.recovered]);
        this.series.filter((s) => s.name === 'Deaths')[0].data.push([x.day, x.total.deaths]);
        this.series.filter((s) => s.name === 'Confirmed')[0].data.push([x.day, x.total.confirmed]);
      });
      this.updateSeriesLine(this.series, false);
    },
    formatGlobalEntrySeries(entries) {
      this.removeSeries();
      entries.forEach((x) => {
        this.series
          .filter((s) => s.name === this.$t('chart.recovered'))[0]
          .data.push([`${x.day} GMT`, x.total.recovered]);
        this.series
          .filter((s) => s.name === this.$t('chart.deaths'))[0]
          .data.push([`${x.day} GMT`, x.total.deaths]);
        this.series
          .filter((s) => s.name === this.$t('chart.confirmed'))[0]
          .data.push([`${x.day} GMT`, x.total.confirmed]);
      });
      this.updateSeriesLine(this.series, true);
    },
    updateSeriesLine(seriesData, annotation) {
      if (this.$refs.realtimeChart) {
        this.$refs.realtimeChart.updateSeries(seriesData, false, true);
        if (annotation) {
          this.addDayAnnotation(seriesData[0].data.length - 1);
        }
      }
    },
    removeSeries() {
      if (this.$refs.realtimeChart) {
        this.series = [
          {
            name: this.$t('chart.deaths'),
            data: [],
          },
          {
            name: this.$t('chart.recovered'),
            data: [],
          },
          {
            name: this.$t('chart.confirmed'),
            data: [],
          },
        ];
      }
    },
    getDateIndex(dateToString) {
      return this.findWithAttr(this.globalDataList, 'day', dateToString);
    },
    findWithAttr(array, attr, value) {
      for (let i = 0; i < array.length; i += 1) {
        if (array[i][attr] === value) {
          return i;
        }
      }
      return -1;
    },
  },
  mounted() {
    this.$store.dispatch(actions.LOAD_GLOBAL_DATA);
  },
};
</script>

<style scoped>
#chart {
  width: 100%;
}

.bottom-entry {
  flex-grow: 1;
  align-self: flex-end;
}
</style>

<style>
.apexcharts-tooltip {
  display: none;
}
</style>
